import { WorkbookColumnBuilders } from '../../@types';
import { BatchRateFormData } from '../../../../shared-jarvis';

/**
 * Column builders for Batch Rate Request
 *
 * These builders transform form data into Excel columns that are applied
 * to EVERY row in the uploaded shipment file.
 *
 * CRITICAL: Column order MUST match ShipmentDataToRate.cs exactly!
 * Order defined in: BS.RateQuality.Functions/Common/Entities/ShipmentDataToRate.cs
 *
 * The order is:
 * 1. Systems To Rate (BG,MG,P44)
 * 2. BG Rating Account#
 * 3. MG P44 Account#
 * 4. Contract Names Filter
 * 5. Notify Email Address
 * 6-29. [Shipment data: PRG through Service Days - from uploaded file]
 * 30-31. [Origin Geo, Dest Geo - from uploaded file, optional]
 * 32. [Mileage - from uploaded file]
 * 33-37. Output columns: Error, API Response(ms), FilePart, Request, Response
 * 38. Targeted Carriers
 * 39-42. Return Customer/Live/Proposal/Volume Rates
 * 43-44. [Is Stackable, Max Stacking Layers - from uploaded file]
 */

// Define columns in the EXACT order RateQuality expects
// This object will be converted to an array to preserve order
export const batchRequestColumnBuilders: Array<{
  columnName: string;
  builder: (formFields: BatchRateFormData) => any;
}> = [
  // 1. Systems To Rate (BG,MG,P44)
  {
    columnName: 'Systems To Rate (BG,MG,P44)',
    builder: () => 'BG',
  },

  // 2. BG Rating Account# (comma-separated list for multiple accounts)
  {
    columnName: 'BG Rating Account#',
    builder: (formFields) =>
      (formFields.accounts ?? [])
        .map((account) => account?.accountNumber)
        .filter(Boolean)
        .join(','),
  },

  // 3. MG P44 Account#
  {
    columnName: 'MG P44 Account#',
    builder: () => '',
  },

  // 4. Contract Names Filter
  {
    columnName: 'Contract Names Filter',
    builder: () => '',
  },

  // 5. Notify Email Address
  {
    columnName: 'Notify Email Address',
    builder: (formFields) =>
      formFields.notifyEmails.map((v: { value: string }) => v.value).join(','),
  },

  // 6-32. Shipment data columns will be inserted here by Excel worker

  // 33. Error
  {
    columnName: 'Error',
    builder: () => '',
  },

  // 34. API Response(ms)
  {
    columnName: 'API Response(ms)',
    builder: () => 0,
  },

  // 35. FilePart
  {
    columnName: 'FilePart',
    builder: () => 1,
  },

  // 36. Request
  {
    columnName: 'Request',
    builder: () => '',
  },

  // 37. Response
  {
    columnName: 'Response',
    builder: () => '',
  },

  // 38. Targeted Carriers
  {
    columnName: 'Targeted Carriers',
    builder: (formFields) =>
      formFields.targetedCarriers
        ?.map((carrier) => carrier.scac || (carrier as any).value)
        .join(',') || '',
  },

  // 39. Return Customer Rates
  {
    columnName: 'Return Customer Rates',
    builder: (formFields) => (formFields.returnCustomerRates ? '1' : '0'),
  },

  // 40. Return Live Contract Rates
  {
    columnName: 'Return Live Contract Rates',
    builder: (formFields) => (formFields.returnLiveContractRates ? '1' : '0'),
  },

  // 41. Return Proposal Contract Rates
  {
    columnName: 'Return Proposal Contract Rates',
    builder: (formFields) =>
      formFields.returnProposalContractRates ? '1' : '0',
  },

  // 42. Return Volume Rates
  {
    columnName: 'Return Volume Rates',
    builder: (formFields) => (formFields.returnVolumeRates ? '1' : '0'),
  },

  // 43-44. Is Stackable, Max Stacking Layers - from uploaded file
];
